import React from "react";
import {
  BUTTON_STYLE_OPTIONS,
  MAX_HERO_BANNER_BUTTONS,
} from "./heroBannerConstants";
import {
  canAddMoreButtons,
  getAvailableStyleOptions,
  getUsedButtonStyles,
  getButtonPreviewClass,
} from "./buttonStyleUtils";
import { createEmptyButton } from "./heroBannerUtils";

const HeroBannerButtonsSection = ({
  buttons,
  onChange,
  onAdd,
  onRemove,
  buttonErrors = {},
  rootError = "",
}) => {
  const usedStyles = getUsedButtonStyles(buttons);
  const canAdd = canAddMoreButtons(buttons);

  const handleAdd = () => {
    if (!canAdd) return;
    onAdd(createEmptyButton(buttons.length + 1));
  };

  return (
    <div className="border rounded p-3 bg-light bg-opacity-50">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h6 className="mb-1">Banner Buttons</h6>
          <p className="text-muted small mb-0">
            Maximum {MAX_HERO_BANNER_BUTTONS} buttons. Each style (Green, Blue,
            Light) can only be used once.
          </p>
        </div>
        {canAdd && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleAdd}
          >
            <i className="ri-add-line align-middle me-1"></i>
            Add Button
          </button>
        )}
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {BUTTON_STYLE_OPTIONS.map((opt) => {
          const isUsed = usedStyles.includes(opt.value);
          return (
            <span
              key={opt.value}
              className={`badge ${isUsed ? "bg-success" : "bg-secondary"}`}
            >
              {opt.label}
              {isUsed ? " ✓" : ""}
            </span>
          );
        })}
        <span className="badge bg-primary-subtle text-primary">
          {buttons.length} / {MAX_HERO_BANNER_BUTTONS}
        </span>
      </div>

      {rootError && (
        <div className="alert alert-danger py-2 small mb-3">{rootError}</div>
      )}

      {buttons.length === 0 ? (
        <div className="text-center text-muted py-3 border rounded bg-white">
          No buttons added. Click &quot;Add Button&quot; to create CTA buttons.
        </div>
      ) : (
        buttons.map((button, index) => {
          const styleOptions = getAvailableStyleOptions(
            buttons,
            index,
            button.buttonStyle
          );
          const fieldErrors = buttonErrors[index] || {};

          return (
            <div
              key={button.clientId}
              className="card mb-2 border shadow-none"
            >
              <div className="card-body p-3">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                  <span className="text-muted small fw-semibold">
                    Button {index + 1}
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className={getButtonPreviewClass(button.buttonStyle)}
                      disabled
                    >
                      {button.buttonLabel || "Preview"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => onRemove(index)}
                      title="Remove button"
                    >
                      <i className="ri-delete-bin-6-line"></i>
                    </button>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">
                      Button Label <span className="required-field">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${
                        fieldErrors.buttonLabel ? "is-invalid" : ""
                      }`}
                      value={button.buttonLabel}
                      onChange={(e) =>
                        onChange(index, { buttonLabel: e.target.value })
                      }
                      placeholder="Enter label"
                    />
                    {fieldErrors.buttonLabel && (
                      <div className="invalid-feedback d-block">
                        {fieldErrors.buttonLabel}
                      </div>
                    )}
                  </div>
                  <div className="col-lg-4 col-md-6">
                    <label className="form-label">
                      Button URL <span className="required-field">*</span>
                    </label>
                    <input
                      type="url"
                      className={`form-control ${
                        fieldErrors.buttonUrl ? "is-invalid" : ""
                      }`}
                      value={button.buttonUrl}
                      onChange={(e) =>
                        onChange(index, { buttonUrl: e.target.value })
                      }
                      placeholder="https://example.com"
                    />
                    {fieldErrors.buttonUrl && (
                      <div className="invalid-feedback d-block">
                        {fieldErrors.buttonUrl}
                      </div>
                    )}
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="form-label">
                      Button Style <span className="required-field">*</span>
                    </label>
                    <select
                      className={`form-select ${
                        fieldErrors.buttonStyle ? "is-invalid" : ""
                      }`}
                      value={button.buttonStyle}
                      onChange={(e) =>
                        onChange(index, { buttonStyle: e.target.value })
                      }
                    >
                      <option value="">Select style</option>
                      {styleOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.buttonStyle && (
                      <div className="invalid-feedback d-block">
                        {fieldErrors.buttonStyle}
                      </div>
                    )}
                  </div>
                  <div className="col-lg-2 col-md-6">
                    <label className="form-label">Button Order</label>
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      max="3"
                      value={button.displayOrder}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default HeroBannerButtonsSection;
